import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaBookOpen, FaGraduationCap, FaQuestionCircle, FaYoutube } from 'react-icons/fa';
import { SiDevdotto, SiFreecodecamp, SiGeeksforgeeks, SiPython, SiW3Schools } from 'react-icons/si';
import { getCookbookLearnMoreOptions, openExternalSearch } from '../../utils/cookbookLearnMore';

function GoogleSearchIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908C16.658 14.015 17.64 11.707 17.64 9.2z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

const LEARN_MORE_ICONS = {
  youtube: FaYoutube,
  google: GoogleSearchIcon,
  geeksforgeeks: SiGeeksforgeeks,
  freecodecamp: SiFreecodecamp,
  devto: SiDevdotto,
  'real-python': SiPython,
  w3schools: SiW3Schools,
  'hacking-with-swift': FaGraduationCap,
  'javascript-info': FaBookOpen,
  'typescript-handbook': FaBookOpen,
  baeldung: FaGraduationCap,
  'microsoft-learn': FaGraduationCap,
  'go-tour': FaBookOpen,
  'rust-book': FaBookOpen,
  'kotlin-docs': FaBookOpen,
  'ruby-docs': FaBookOpen,
  learncpp: FaBookOpen
};

const LEARN_MORE_ICON_CLASS = {
  youtube: 'roadmap-list__topic-learn-more-option-icon--youtube',
  google: 'roadmap-list__topic-learn-more-option-icon--google',
  geeksforgeeks: 'roadmap-list__topic-learn-more-option-icon--geeksforgeeks',
  freecodecamp: 'roadmap-list__topic-learn-more-option-icon--freecodecamp',
  devto: 'roadmap-list__topic-learn-more-option-icon--devto'
};

function LearnMoreOptionIcon({ icon, className }) {
  const Icon = LEARN_MORE_ICONS[icon] || FaGraduationCap;
  return <Icon className={className} aria-hidden />;
}

function CookbookLearnMoreMenu({ track, topicTitle, onPointerEventStop }) {
  const menuId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const menuOptions = useMemo(
    () => getCookbookLearnMoreOptions(track, topicTitle),
    [track, topicTitle]
  );
  const canOpen = menuOptions.length > 0;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const stopRowToggle = (event) => {
    event.stopPropagation();
    onPointerEventStop?.(event);
  };

  const handleToggle = (event) => {
    stopRowToggle(event);
    if (!canOpen) return;
    setOpen((current) => !current);
  };

  const handleOptionClick = (event, url) => {
    stopRowToggle(event);
    openExternalSearch(url);
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={[
        'roadmap-list__topic-learn-more',
        open ? 'roadmap-list__topic-learn-more--open' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="roadmap-list__topic-action roadmap-list__topic-action--study roadmap-list__topic-action--icon-only roadmap-list__topic-learn-more-btn"
        onClick={handleToggle}
        onKeyDown={stopRowToggle}
        disabled={!canOpen}
        title={`Find tutorials for ${topicTitle}`}
        aria-label={`Find tutorials for ${topicTitle}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <FaQuestionCircle className="roadmap-list__topic-action-icon-svg" aria-hidden />
      </button>

      {open ? (
        <div
          id={menuId}
          className="roadmap-list__topic-learn-more-menu"
          role="menu"
          aria-label={`Tutorial options for ${topicTitle}`}
        >
          {menuOptions.map((option) => {
            const iconClassName = [
              'roadmap-list__topic-learn-more-option-icon',
              LEARN_MORE_ICON_CLASS[option.icon]
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={option.id}
                type="button"
                className="roadmap-list__topic-learn-more-option"
                role="menuitem"
                onClick={(event) => handleOptionClick(event, option.url)}
              >
                <LearnMoreOptionIcon icon={option.icon} className={iconClassName} />
                <span className="roadmap-list__topic-learn-more-option-label">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default CookbookLearnMoreMenu;
